import { useCallback } from "react";

import {
  ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS,
  ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS_KEYS as ec,
} from "@frontend/common";

import { EXPEDITED_PROGRAM } from "@FillInTableContainer/shared/constants/expeditedProgramKey.constants";
import {
  DEFAULT_FOCUS_STATUS,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { TableInfoProps } from "../ExpeditedProgramContentsTable";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorTableReturn {
  handleClickExpeditedProgramContentsDeleteButton: () => void;
  handleKeyDownToDeleteExpeditedProgramContentsTable: (
    event: KeyboardEvent,
  ) => void;
}

export const useExpeditedProgramContentsTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const { groupTableKey, tableKey, groupTableIndex, tableIndex } =
    tablePropsInfo;

  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const articleSection = EXPEDITED_PROGRAM;

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteTable } = useCheckDisabledDelete();

  const expeditedProgramContentsLength =
    entryData[articleSection]?.[ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS]
      ?.length || 0;

  const handleClearTable = useCallback(() => {
    const clearExpeditedProgramContents = (): ArticleEntryData => {
      return {
        ...entryData,
        [EXPEDITED_PROGRAM]: {
          ...entryData[EXPEDITED_PROGRAM],
          [ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS]: entryData[
            EXPEDITED_PROGRAM
          ]?.[ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  [ec.updateDate]: {
                    ...tableInfo?.[ec.updateDate],
                    value: [],
                  },
                  [ec.designationStatus]: {
                    ...tableInfo?.[ec.designationStatus],
                    value: [],
                  },
                  [ec.designatedCompanyName]: {
                    ...tableInfo?.[ec.designatedCompanyName],
                    value: [],
                  },
                  [ec.designatedProduct]: {
                    ...tableInfo?.[ec.designatedProduct],
                    value: [],
                  },
                  [ec.regulatoryAgency]: {
                    ...tableInfo?.[ec.regulatoryAgency],
                    value: [],
                  },
                  [ec.expeditedProgram]: {
                    ...tableInfo?.[ec.expeditedProgram],
                    value: [],
                  },
                  [ec.designatedIndication]: {
                    ...tableInfo?.[ec.designatedIndication],
                    value: [],
                  },
                  [ec.designationBasisClinicalSignificance]: {
                    ...tableInfo?.[ec.designationBasisClinicalSignificance],
                    value: [],
                  },
                  [ec.programBenefitsIncentives]: {
                    ...tableInfo?.[ec.programBenefitsIncentives],
                    value: [],
                  },
                };
              }
              return tableInfo;
            },
          ),
        },
      };
    };

    if (info.groupTableKey === ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS) {
      setEntryData(clearExpeditedProgramContents());
      changeCurrentHistory({
        entryData: clearExpeditedProgramContents(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      if (expeditedProgramContentsLength > 1 && !checkDisabledDeleteTable()) {
        changeFocusStatus({ type: "delete", container: "table" });
      } else {
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
      }
    }
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    checkDisabledDeleteTable,
    expeditedProgramContentsLength,
    entryData,
    groupTableIndex,
    groupTableKey,
    info.groupTableKey,
    info.tableIndex,
    setEntryData,
    tableIndex,
    tableKey,
  ]);

  const handleDeleteTable = useCallback(() => {
    if (checkDisabledDeleteTable()) {
      return;
    }
    if (expeditedProgramContentsLength > 1) {
      changeFocusStatus(DEFAULT_FOCUS_STATUS);

      const deleteExpeditedProgramContents = (): ArticleEntryData => {
        return {
          ...entryData,
          [EXPEDITED_PROGRAM]: {
            ...entryData[EXPEDITED_PROGRAM],
            [ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS]: entryData[
              EXPEDITED_PROGRAM
            ]?.[ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS]?.filter(
              (tableInfo, tableInfoIndex) => tableInfoIndex !== info.tableIndex,
            ),
          },
        };
      };
      if (info.groupTableKey === ENTRY_DATA_EXPEDITED_PROGRAM_CONTENTS) {
        setEntryData(deleteExpeditedProgramContents());
        changeCurrentHistory({
          entryData: deleteExpeditedProgramContents(),
          changedDataInfo: {
            target: "table",
            action: "delete",
            position: {
              groupTableKey,
              groupTableIndex,
              tableKey,
              tableIndex,
              groupShellKey: null,
              groupShellIndex: null,
              shellKey: null,
              shellIndex: null,
              valueIndex: null,
              value: null,
            },
          },
        });
        changeInfo(DEFAULT_POSITION_INFO);
      }
    }
  }, [
    expeditedProgramContentsLength,
    entryData,
    groupTableIndex,
    groupTableKey,
    info,
    tableIndex,
    tableKey,
    setEntryData,
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    checkDisabledDeleteTable,
  ]);

  const handleClickExpeditedProgramContentsDeleteButton = () => {
    if (focusStatus.type === "clear") handleClearTable();
    if (focusStatus.type === "delete") handleDeleteTable();
  };

  const handleKeyDownToDeleteExpeditedProgramContentsTable = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearTable();
      }
      if (focusStatus.type === "delete" && event.key === "Delete") {
        handleDeleteTable();
      }
      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    },
    [
      focusStatus.type,
      changeValidatedInfo,
      handleClearTable,
      handleDeleteTable,
    ],
  );

  return {
    handleClickExpeditedProgramContentsDeleteButton,
    handleKeyDownToDeleteExpeditedProgramContentsTable,
  };
};
